import React, { useContext, useEffect, useState, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { motion, useDragControls, useMotionValue } from "motion/react";
import { X } from "lucide-react";
import {
  TableCellsIcon,
  ArrowDownTrayIcon,
  ClipboardDocumentIcon,
  XMarkIcon,
  CheckIcon,
} from "@heroicons/react/24/outline";
import cadviewer from "cadviewer";
import { CadviewerContext } from "../../context/Cadviewer.Context";
import useConfig from "../../demos/hooks/useConfig";
import { SelectedSpacesDrawer, QuickActionsMenu } from "./components";
import { useSpaceToolsStore } from "../SpaceTools/stores/spaceToolsStore";
import {
  getSelectedSpacesData,
  calculateFinancialSummary,
  calculateOpportunityCost,
  generateQuickActionMessage,
} from "../../modules/ai/components/AIChatDrawer/utils/quickActionCalculations";
import {
  TableData,
  formatCellValue,
  exportTableAsCSV,
  exportTableAsXLSX,
  copyTableForExcel,
} from "../../modules/ai/types/table";

// Position styles mapping
const positionStyles: Record<string, string> = {
  "bottom-left": "fixed bottom-4 left-4 z-[60]",
  "bottom-right": "fixed bottom-20 right-4 z-[60]",
  "top-right": "fixed top-20 right-4 z-[60]",
};

// Result Modal Component
interface ResultModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  message: string;
  tableData: TableData | null;
}

const ResultModal: React.FC<ResultModalProps> = ({
  isOpen,
  onClose,
  title,
  message,
  tableData,
}) => {
  const [copiedToClipboard, setCopiedToClipboard] = useState(false);

  // Drag and resize state
  const [modalWidth, setModalWidth] = useState(1200);
  const [modalHeight, setModalHeight] = useState(700);
  const [resizeDirection, setResizeDirection] = useState<string | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const initialMousePos = useRef({ x: 0, y: 0 });
  const initialSize = useRef({ width: 0, height: 0 });
  const initialPosition = useRef({ x: 0, y: 0 });

  // Motion values for position
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  // Drag controls
  const dragControls = useDragControls();

  // Resize handlers - MUST be before any conditional returns
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!resizeDirection) return;

      const deltaX = e.clientX - initialMousePos.current.x;
      const deltaY = e.clientY - initialMousePos.current.y;

      let newWidth = modalWidth;
      let newHeight = modalHeight;
      let newX = x.get();
      let newY = y.get();

      // Handle horizontal resizing
      if (resizeDirection.includes('left')) {
        newWidth = initialSize.current.width - deltaX;
        newX = initialPosition.current.x + deltaX;
      } else if (resizeDirection.includes('right')) {
        newWidth = initialSize.current.width + deltaX;
      }

      // Handle vertical resizing
      if (resizeDirection.includes('top')) {
        newHeight = initialSize.current.height - deltaY;
        newY = initialPosition.current.y + deltaY;
      } else if (resizeDirection.includes('bottom')) {
        newHeight = initialSize.current.height + deltaY;
      }

      // Apply max constraints only (no min constraints)
      newWidth = Math.min(newWidth, window.innerWidth - 40);
      newHeight = Math.min(newHeight, window.innerHeight - 40);

      setModalWidth(newWidth);
      setModalHeight(newHeight);

      if (resizeDirection.includes('left') || resizeDirection.includes('top')) {
        x.set(newX);
        y.set(newY);
      }
    };

    const handleMouseUp = () => {
      setResizeDirection(null);
    };

    if (resizeDirection) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [resizeDirection, modalWidth, modalHeight, x, y]);

  // Reset position when modal opens
  useEffect(() => {
    if (isOpen) {
      x.set(0);
      y.set(0);
    }
  }, [isOpen, x, y]);

  // Drag constraints - allow dragging to all screen edges
  const dragConstraints = useMemo(
    () => ({
      left: -(window.innerWidth - 100),
      right: window.innerWidth - 100,
      top: -(window.innerHeight - 100),
      bottom: window.innerHeight - 100,
    }),
    []
  );

  // Early returns AFTER all hooks
  if (!tableData || !isOpen) return null;

  const { labels, data } = tableData;
  const fieldNames = Object.keys(labels);

  // Handle CSV export
  const handleExportCSV = () => {
    const csvContent = exportTableAsCSV(tableData);
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    const filename = `${title.replace(/\s+/g, "_")}_export.csv`;
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Handle Excel export as native .xlsx file
  const handleExportExcel = async () => {
    const filename = `${title.replace(/\s+/g, "_")}_export.xlsx`;
    await exportTableAsXLSX(tableData, filename);
  };

  // Handle copy to clipboard for Excel paste
  const handleCopyForExcel = async () => {
    try {
      const tsvContent = copyTableForExcel(tableData);
      await navigator.clipboard.writeText(tsvContent);
      setCopiedToClipboard(true);
      setTimeout(() => setCopiedToClipboard(false), 2000);
    } catch (error) {
      console.error("Failed to copy to clipboard:", error);
      alert("Failed to copy to clipboard. Please try again.");
    }
  };

  // Handle resize start
  const handleResizeStart = (direction: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setResizeDirection(direction);
    initialMousePos.current = { x: e.clientX, y: e.clientY };
    initialSize.current = { width: modalWidth, height: modalHeight };
    initialPosition.current = { x: x.get(), y: y.get() };
  };

  return createPortal(
    <div className="fixed inset-0 z-[1000000003] w-full h-full" style={{ pointerEvents: 'none' }}>
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" style={{ pointerEvents: 'none' }}>
        <span
          className="hidden sm:inline-block sm:align-middle sm:h-screen"
          aria-hidden="true"
        >
          &#8203;
        </span>

        {/* Modal - now draggable and resizable */}
        <motion.div
          ref={modalRef}
          drag
          dragListener={false}
          dragControls={dragControls}
          dragConstraints={dragConstraints}
          dragElastic={0}
          dragMomentum={false}
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            marginLeft: -(modalWidth / 2),
            marginTop: -(modalHeight / 2),
            x,
            y,
            width: modalWidth,
            height: modalHeight,
            maxWidth: window.innerWidth - 40,
            maxHeight: window.innerHeight - 40,
            pointerEvents: 'auto',
          }}
          className="bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 relative flex flex-col"
        >
          {/* Resize Handles - 4 sides */}

          {/* Top */}
          <div
            onMouseDown={(e) => handleResizeStart('top', e)}
            style={{
              position: 'absolute',
              left: 8,
              right: 8,
              top: 0,
              height: 8,
              cursor: 'ns-resize',
              zIndex: 10,
            }}
            aria-label="Resize modal height from top"
          />

          {/* Right */}
          <div
            onMouseDown={(e) => handleResizeStart('right', e)}
            style={{
              position: 'absolute',
              right: 0,
              top: 8,
              bottom: 8,
              width: 8,
              cursor: 'ew-resize',
              zIndex: 10,
            }}
            aria-label="Resize modal width from right"
          />

          {/* Bottom */}
          <div
            onMouseDown={(e) => handleResizeStart('bottom', e)}
            style={{
              position: 'absolute',
              left: 8,
              right: 8,
              bottom: 0,
              height: 8,
              cursor: 'ns-resize',
              zIndex: 10,
            }}
            aria-label="Resize modal height from bottom"
          />

          {/* Left */}
          <div
            onMouseDown={(e) => handleResizeStart('left', e)}
            style={{
              position: 'absolute',
              left: 0,
              top: 8,
              bottom: 8,
              width: 8,
              cursor: 'ew-resize',
              zIndex: 10,
            }}
            aria-label="Resize modal width from left"
          />

          {/* Resize Handles - 4 corners */}

          {/* Top-Left Corner */}
          <div
            onMouseDown={(e) => handleResizeStart('top-left', e)}
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              width: 16,
              height: 16,
              cursor: 'nwse-resize',
              zIndex: 11,
            }}
            aria-label="Resize modal from top-left corner"
          />

          {/* Top-Right Corner */}
          <div
            onMouseDown={(e) => handleResizeStart('top-right', e)}
            style={{
              position: 'absolute',
              right: 0,
              top: 0,
              width: 16,
              height: 16,
              cursor: 'nesw-resize',
              zIndex: 11,
            }}
            aria-label="Resize modal from top-right corner"
          />

          {/* Bottom-Left Corner */}
          <div
            onMouseDown={(e) => handleResizeStart('bottom-left', e)}
            style={{
              position: 'absolute',
              left: 0,
              bottom: 0,
              width: 16,
              height: 16,
              cursor: 'nesw-resize',
              zIndex: 11,
            }}
            aria-label="Resize modal from bottom-left corner"
          />

          {/* Bottom-Right Corner */}
          <div
            onMouseDown={(e) => handleResizeStart('bottom-right', e)}
            style={{
              position: 'absolute',
              right: 0,
              bottom: 0,
              width: 16,
              height: 16,
              cursor: 'nwse-resize',
              zIndex: 11,
            }}
            aria-label="Resize modal from bottom-right corner"
          />

          {/* Header - Draggable - Fixed */}
          <div
            className="flex-none px-6 py-4 border-b cursor-move select-none"
            style={{
              background: "linear-gradient(to right, #0f3557, #0a2338)",
              borderBottomColor: "#072033",
              userSelect: "none",
            }}
            onPointerDown={(e) => dragControls.start(e)}
          >
            <div className="flex items-center justify-between">
              <h3 className="text-xl leading-6 font-bold text-white flex items-center gap-2">
                <TableCellsIcon className="h-6 w-6" />
                {title}
              </h3>
              <div className="flex items-center gap-2">
                {/* Export buttons */}
                <button
                  onClick={handleExportCSV}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-md text-sm font-medium transition-colors"
                  title="Export as CSV"
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                  CSV
                </button>
                <button
                  onClick={handleExportExcel}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-md text-sm font-medium transition-colors"
                  title="Export as Excel"
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                  Excel
                </button>
                <button
                  onClick={handleCopyForExcel}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-md text-sm font-medium transition-colors"
                  title="Copy for Excel paste"
                >
                  {copiedToClipboard ? (
                    <>
                      <CheckIcon className="h-4 w-4" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <ClipboardDocumentIcon className="h-4 w-4" />
                      Copy
                    </>
                  )}
                </button>
                <button
                  onClick={onClose}
                  className="text-white transition-colors ml-2"
                  style={{ opacity: 0.9 }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.opacity = "0.7";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.opacity = "0.9";
                  }}
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>
            <p
              className="mt-1 text-sm"
              style={{ color: "rgba(255, 255, 255, 0.8)" }}
            >
              {message}
            </p>
          </div>

          {/* Content - Scrollable */}
          <div className="flex-1 overflow-auto" style={{ minHeight: 0 }}>
            <div className="px-6 py-4">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 border border-gray-300">
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr>
                      {fieldNames.map((fieldName) => (
                        <th
                          key={fieldName}
                          className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 bg-gray-50"
                        >
                          {labels[fieldName]}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {data.map((row, rowIndex) => (
                      <tr
                        key={rowIndex}
                        className={
                          row._is_summary
                            ? "font-semibold"
                            : "hover:bg-gray-50"
                        }
                        style={row._is_summary ? { backgroundColor: 'rgba(15, 53, 87, 0.1)' } : {}}
                      >
                        {fieldNames.map((fieldName) => {
                          const value = row[fieldName];
                          const formattedValue = formatCellValue(
                            fieldName,
                            value
                          );

                          return (
                            <td
                              key={fieldName}
                              className="px-4 py-3 text-sm text-gray-700 border-r border-gray-200"
                            >
                              {formattedValue}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Footer - Fixed */}
          <div className="flex-none bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-between items-center">
            <div className="text-xs text-gray-500">
              {copiedToClipboard && (
                <span className="text-green-600 font-medium">
                  ✓ Copied to clipboard! You can now paste into Excel.
                </span>
              )}
            </div>
            <button
              onClick={onClose}
              className="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 sm:w-auto sm:text-sm"
              style={{ backgroundColor: "#0f3557" }}
            >
              Close
            </button>
          </div>
        </motion.div>
      </div>
    </div>,
    document.body
  );
};

const SpaceSelectionPanel: React.FC = () => {
  const { config } = useConfig();
  const {
    multiSelectArray,
    setMultiSelectArray,
    plansInfo,
    selectedPlan,
    selectedPlanDetail,
    enableDragSelectSpaces,
  } = useContext(CadviewerContext);

  // Space Tools Store
  const { activePanel, closePanel } = useSpaceToolsStore();

  // Only render if this panel is active
  const isOpen = activePanel === 'spaceSelection';

  // State
  const [selectedSpacesChips, setSelectedSpacesChips] = useState<string[]>([]);
  const [showQuickActionsMenu, setShowQuickActionsMenu] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false); // For collapsing/expanding the widget
  const [resultModal, setResultModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    tableData: TableData | null;
  }>({
    isOpen: false,
    title: "",
    message: "",
    tableData: null,
  });

  // Drag controls for making the panel draggable
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const dragControls = useDragControls();

  // Drag constraints to keep panel within screen bounds
  const dragConstraints = useMemo(
    () => ({
      left: -(window.innerWidth - 100),
      right: window.innerWidth - 100,
      top: -(window.innerHeight - 100),
      bottom: window.innerHeight - 100,
    }),
    []
  );

  // Note: We do NOT automatically clear selectedSpacesChips when multiSelectArray becomes empty
  // because they are independent states:
  // - multiSelectArray: temporary selection from drag-select
  // - selectedSpacesChips: permanent user-saved selection

  // Handlers
  const handleAddSelectedSpaces = () => {
    if (multiSelectArray.length > 0) {
      // Merge with existing chips, avoiding duplicates
      const combinedChips = [...selectedSpacesChips, ...multiSelectArray];
      const newChips = Array.from(new Set(combinedChips));
      setSelectedSpacesChips(newChips);
      // Clear multiSelectArray after adding
      setMultiSelectArray([]);
    }
  };

  const handleRemoveSpaceChip = (spaceId: string) => {
    setSelectedSpacesChips((prev) => prev.filter((id) => id !== spaceId));
  };

  const handleClearAllSpaceChips = () => {
    cadviewer.cvjs_clearSpaceLayer(); // Clear visual highlights on canvas
    cadviewer.cvjs_clearMultiSelectArrays(); // Clear CADViewer multiselect arrays
    setSelectedSpacesChips([]);
    setMultiSelectArray([]);
  };

  const handleToggleQuickActionsMenu = () => {
    setShowQuickActionsMenu(!showQuickActionsMenu);
  };

  const handleTriggerAction = (actionName: string) => {
    setShowQuickActionsMenu(false);

    // Check if we have selected spaces
    if (selectedSpacesChips.length === 0) {
      alert("Please select at least one space to perform this analysis.");
      return;
    }

    // DEBUG: Log all the data to help troubleshoot
    console.log("=== DEBUG: Quick Action Triggered ===");
    console.log("1. Selected Space Chips:", selectedSpacesChips);
    console.log("2. plansInfo exists:", !!plansInfo);
    console.log("3. plansInfo:", plansInfo);
    if (plansInfo?.spaces) {
      console.log("4. Total spaces in plansInfo:", plansInfo.spaces.length);
      console.log("5. First 5 space numbers from plansInfo:",
        plansInfo.spaces.slice(0, 5).map(s => s.spaceNumber)
      );
      console.log("6. Sample space object:", plansInfo.spaces[0]);
    }

    // Get space data from plansInfo
    const spacesData = getSelectedSpacesData(plansInfo, selectedSpacesChips);

    console.log("7. Spaces data found:", spacesData.length);
    console.log("8. Spaces data:", spacesData);
    console.log("=====================================");

    if (spacesData.length === 0) {
      alert("Could not find data for the selected spaces. Please ensure a plan is loaded and try again.");
      return;
    }

    let tableData = null;
    let messageContent = "";
    let modalTitle = "";

    if (actionName === "Financial Summary") {
      tableData = calculateFinancialSummary(spacesData);
      messageContent = generateQuickActionMessage(
        "Financial Summary",
        spacesData.length,
        selectedPlan?.propertyName
      );
      modalTitle = "Financial Summary";
    } else if (actionName === "Opportunity Cost") {
      tableData = calculateOpportunityCost(spacesData);
      messageContent = generateQuickActionMessage(
        "Opportunity Cost",
        spacesData.length,
        selectedPlan?.propertyName
      );
      modalTitle = "Opportunity Cost Analysis";
    }

    if (tableData) {
      setResultModal({
        isOpen: true,
        title: modalTitle,
        message: messageContent,
        tableData,
      });
    }
  };

  const handleCloseResultModal = () => {
    setResultModal({
      isOpen: false,
      title: "",
      message: "",
      tableData: null,
    });
  };

  const positionClass = positionStyles[config.spaceSelectionPosition] || positionStyles["bottom-left"];

  // Don't render if panel is not open
  if (!isOpen) return null;

  return (
    <>
      {/* Space Selection Widget - Draggable */}
      <motion.div
        drag
        dragListener={false}
        dragControls={dragControls}
        dragConstraints={dragConstraints}
        dragElastic={0}
        dragMomentum={false}
        style={{ x, y }}
        className={positionClass}
      >
        <div className="bg-white rounded-lg shadow-xl border-2 overflow-hidden max-w-md" style={{ borderColor: '#0f3557' }}>
          {/* Title Bar - Always visible with collapse/expand button - Draggable handle */}
          <div
            className="px-3 py-2 text-white flex items-center justify-between cursor-move"
            style={{ background: 'linear-gradient(to right, #0f3557, #0a2a42)', userSelect: 'none' }}
            onPointerDown={(e) => dragControls.start(e)}
          >
            <h3 className="text-xs font-bold flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
              </svg>
              Space Selection & Quick Analysis
            </h3>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setIsCollapsed(!isCollapsed)}
                className="p-1 rounded transition-colors"
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#0a2a42'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                title={isCollapsed ? "Expand" : "Collapse"}
              >
                {isCollapsed ? (
                  // Plus icon (expand)
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                  </svg>
                ) : (
                  // Minus icon (collapse)
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5 10a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1z" clipRule="evenodd" />
                  </svg>
                )}
              </button>
              <button
                onClick={closePanel}
                className="p-1 rounded transition-colors"
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#0a2a42'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                title="Close"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Content - Hidden when collapsed */}
          {!isCollapsed && (
            <>
              {/* Temporary Selection (from drag-select) */}
              {multiSelectArray.length > 0 && (
                <div className="px-3 py-2 bg-orange-50 border-b border-orange-200 animate-pulse">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-orange-700 font-semibold flex items-center gap-1">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      {multiSelectArray.length} space{multiSelectArray.length > 1 ? "s" : ""} selected
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        cadviewer.cvjs_clearSpaceLayer(); // Clear visual highlights on canvas
                        cadviewer.cvjs_clearMultiSelectArrays(); // Clear CADViewer multiselect arrays
                        setMultiSelectArray([]); // Clear context state
                      }}
                      className="flex-1 px-3 py-1 bg-gray-500 text-white text-xs font-bold rounded hover:bg-gray-600 transition shadow-sm"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleAddSelectedSpaces}
                      className="flex-1 px-3 py-1 bg-orange-600 text-white text-xs font-bold rounded hover:bg-orange-700 transition shadow-sm"
                    >
                      + Add to List
                    </button>
                  </div>
                </div>
              )}

              {/* Selected Spaces Drawer */}
              <SelectedSpacesDrawer
                selectedSpacesChips={selectedSpacesChips}
                showQuickActionsMenu={showQuickActionsMenu}
                onToggleQuickActionsMenu={handleToggleQuickActionsMenu}
                onRemoveSpaceChip={handleRemoveSpaceChip}
                onClearAllSpaceChips={handleClearAllSpaceChips}
              />

              {/* Empty state with instructions */}
              {selectedSpacesChips.length === 0 && multiSelectArray.length === 0 && (
                <div className="px-4 py-4 text-center bg-gray-50">
                  <div className="mb-3">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <p className="text-xs text-gray-600 font-medium mb-3">
                    No spaces selected yet
                  </p>
                  <div className="text-xs text-gray-500 mb-3 text-left space-y-1">
                    <p className="flex items-start gap-2">
                      <span className="font-bold" style={{ color: '#0f3557' }}>1.</span>
                      <span>Click the button below to enable drag-select</span>
                    </p>
                    <p className="flex items-start gap-2">
                      <span className="font-bold" style={{ color: '#0f3557' }}>2.</span>
                      <span>Drag on the canvas to select spaces</span>
                    </p>
                    <p className="flex items-start gap-2">
                      <span className="font-bold" style={{ color: '#0f3557' }}>3.</span>
                      <span>Click "Add to List" to save your selection</span>
                    </p>
                    <p className="flex items-start gap-2">
                      <span className="font-bold" style={{ color: '#0f3557' }}>4.</span>
                      <span>Use Quick Actions for analysis</span>
                    </p>
                  </div>
                  <button
                    onClick={enableDragSelectSpaces}
                    className="w-full px-3 py-2 text-white text-xs font-bold rounded transition shadow-md flex items-center justify-center gap-2"
                    style={{ backgroundColor: '#0f3557' }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#0a2a42'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#0f3557'}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z" />
                    </svg>
                    Enable Drag-Select Mode
                  </button>
                  <button
                    onClick={() => {
                      cadviewer.cvjs_clearSpaceLayer();
                      cadviewer.cvjs_clearMultiSelectArrays();
                    }}
                    className="w-full px-3 py-2 text-gray-600 text-xs font-bold rounded transition shadow-sm flex items-center justify-center gap-2 mt-2 border border-gray-300 bg-white hover:bg-gray-100"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                    Clear Highlights
                  </button>
                </div>
              )}
            </>
          )}

          {/* Quick Actions Menu */}
          {config.enableQuickActions && (
            <QuickActionsMenu
              showQuickActionsMenu={showQuickActionsMenu}
              onTriggerAction={handleTriggerAction}
              onClose={() => setShowQuickActionsMenu(false)}
              actionsConfig={config.quickActionsConfig}
            />
          )}
        </div>
      </motion.div>

      {/* Result Modal */}
      <ResultModal
        isOpen={resultModal.isOpen}
        onClose={handleCloseResultModal}
        title={resultModal.title}
        message={resultModal.message}
        tableData={resultModal.tableData}
      />
    </>
  );
};

export default SpaceSelectionPanel;
